<template>
  <LoadingContainer :is-loading="isLoading">
    <form class="has-sticky-submit" @submit.prevent="submit">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <div class="title is-6 is-marginless">
              Promo Cards - Edit
              <span class="tag">Επεξεργασία</span>
            </div>
          </div>

          <div class="card-header-icon">
            <div class="field has-addons is-marginless">
              <div class="control">
                <RouterLink
                  :to="{ name: 'content.home.promo-cards.list' }"
                  class="button"
                >
                  <span class="icon is-small">
                    <i class="fa fa-list" />
                  </span>
                  <span>Λίστα</span>
                </RouterLink>
              </div>

              <div class="control">
                <button type="button" class="button" @click="askToDelete">
                  <span class="icon is-small">
                    <i class="fa fa-trash" />
                  </span>
                  <span>Διαγραφή</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card-content">
          <div class="columns is-multiline">
            <!-- Image (WEBP) -->
            <div class="column is-full">
              <AddPhotos
                :title="'Εικόνα (WEBP)'"
                :preview="preview"
                :show-error="errors.has('image')"
                rule="image/webp"
                @addPhotos="handleAddPhotos"
              />
              <p class="help">Το αρχείο πρέπει να είναι WEBP.</p>
            </div>

            <!-- Title EL -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EL) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_el"
                    class="input"
                    type="text"
                    name="title_el"
                    maxlength="255"
                  />
                  <p v-show="errors.has('title_el')" class="help is-danger">
                    Εισάγετε ελληνικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <!-- Title EN -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EN) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_en"
                    class="input"
                    type="text"
                    name="title_en"
                    maxlength="255"
                  />
                  <p v-show="errors.has('title_en')" class="help is-danger">
                    Εισάγετε αγγλικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <!-- Content EL -->
            <div class="column is-full">
              <div class="field">
                <label class="label">Περιγραφή (EL)</label>
                <div class="control">
                  <textarea
                    v-model.trim="form.content_el"
                    class="textarea"
                    name="content_el"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <!-- Content EN -->
            <div class="column is-full">
              <div class="field">
                <label class="label">Περιγραφή (EN)</label>
                <div class="control">
                  <textarea
                    v-model.trim="form.content_en"
                    class="textarea"
                    name="content_en"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <!-- Link -->
            <div class="column is-full">
              <div class="field">
                <label class="label">Link (URL) *</label>
                <div class="control">
                  <input
                    v-validate="'required|url'"
                    v-model.trim="form.link"
                    class="input"
                    type="text"
                    name="link"
                    placeholder="https://..."
                    maxlength="2048"
                  />
                  <p v-show="errors.has('link')" class="help is-danger">
                    Εισάγετε έγκυρο link
                  </p>
                </div>
              </div>
            </div>

            <!-- Link text EL -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Κείμενο Συνδέσμου (EL)</label>
                <div class="control">
                  <input
                    v-model.trim="form.link_text_el"
                    class="input"
                    type="text"
                    name="link_text_el"
                    maxlength="255"
                  />
                </div>
              </div>
            </div>

            <!-- Link text EN -->
            <div class="column is-half">
              <div class="field">
                <label class="label">Κείμενο Συνδέσμου (EN)</label>
                <div class="control">
                  <input
                    v-model.trim="form.link_text_en"
                    class="input"
                    type="text"
                    name="link_text_en"
                    maxlength="255"
                  />
                </div>
              </div>
            </div>

            <!-- Active -->
            <div class="column is-full">
              <div class="field">
                <label class="label">
                  Εμφανές *
                  <span class="tag is-rounded ml-2">Έως 2 ενεργές</span>
                </label>
                <div class="control">
                  <ToggleButton
                    :sync="true"
                    :value="form.is_active"
                    :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                    :width="30"
                    :height="15"
                    @change="updateActive"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StickyFormFooter>
        <template #left>
          <FormSubmit :is-saving="isSaving" @reset="reset" />
        </template>

        <template v-if="form.link" #right>
          <a
            v-tooltip="'Προβολή στο e-shop'"
            :href="form.link"
            target="_blank"
            rel="noopener"
          >
            Προβολή link στο e-shop
            <i class="fa fa-arrow-right" />
          </a>
        </template>
      </StickyFormFooter>
    </form>

    <Confirmation
      :model-name="form.title_el"
      :is-open="isDeleteOpen"
      description="Είστε βέβαιος για τη διαγραφή αυτής της κάρτας;"
      @closeModal="closeDeleteModal"
    />
  </LoadingContainer>
</template>

<script>
import request from '@/utils/request';
import StickyFormFooter from '@/views/components/StickyFormFooter';
import LoadingContainer from '@/views/components/LoadingContainer';
import FormSubmit from '@/views/components/FormSubmit';
import Confirmation from '@/views/components/Confirmation';
import AddPhotos from '@/views/components/AddPhotos';

export default {
  name: 'PromoCardEdit',
  components: {
    LoadingContainer,
    StickyFormFooter,
    FormSubmit,
    Confirmation,
    AddPhotos,
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeleteOpen: false,
      original: null,
      form: {
        id: null,
        title_el: '',
        title_en: '',
        content_el: '',
        content_en: '',
        link: '',
        link_text_el: '',
        link_text_en: '',
        is_active: false,
        image: null,
      },
      preview: '',
    };
  },

  computed: {
    imageUrl() {
      return this.original && this.original.image_url
        ? this.original.image_url
        : '';
    },
  },

  metaInfo: { title: 'Promo Cards - Edit' },

  watch: {
    '$route.params.id': {
      immediate: true,
      async handler() {
        await this.fetchItem();
      },
    },
  },

  methods: {
    async fetchItem() {
      this.isLoading = true;
      let notFound = false;

      try {
        // No show endpoint; fetch list, then pick one
        const { data } = await request.get('/home/promo-cards');
        const paramId = String(this.$route.params.id || '').trim();

        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        }

        const found = (list || []).find(x => String(x.id) === paramId);

        if (!found) {
          notFound = true;
        } else {
          this.original = found;
          this.form = {
            id: String(found.id),
            title_el: found.title_el || '',
            title_en: found.title_en || '',
            content_el: found.content_el || '',
            content_en: found.content_en || '',
            link: found.link || '',
            link_text_el: found.link_text_el || '',
            link_text_en: found.link_text_en || '',
            is_active: !!found.is_active,
            image: null,
          };
          this.preview = this.imageUrl || '';
        }
      } catch (err) {
        const msg = (err && err.message) || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg,
        });
      }

      this.isLoading = false;

      if (notFound) {
        this.$notify({
          type: 'error',
          title: 'Σφάλμα',
          text: 'Η κάρτα δεν βρέθηκε',
        });
        this.$router
          .push({ name: 'content.home.promo-cards.list' })
          .catch(() => {});
      }
    },

    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    handleAddPhotos(image) {
      this.form.image = image;

      const reader = new FileReader();
      this.$validator
        .validateAll()
        .then(() => {
          reader.onload = e => {
            this.preview = e && e.target ? e.target.result : '';
            this.errors.remove('image');
          };
          reader.readAsDataURL(image);
        })
        .catch(() => {});
    },

    async submit() {
      const ok = await this.$validator.validateAll();
      if (!ok) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Ελέγξτε τα πεδία της φόρμας',
        });
        return;
      }

      this.isSaving = true;
      try {
        const fd = new FormData();
        fd.append('_method', 'PATCH');
        fd.append('title_el', this.form.title_el || '');
        fd.append('title_en', this.form.title_en || '');
        fd.append('content_el', this.form.content_el || '');
        fd.append('content_en', this.form.content_en || '');
        fd.append('link', this.form.link || '');
        fd.append('link_text_el', this.form.link_text_el || '');
        fd.append('link_text_en', this.form.link_text_en || '');
        fd.append('is_active', this.form.is_active ? '1' : '0');

        if (this.form.image) {
          fd.append('image', this.form.image);
        }

        const resp = await request.post(
          `/home/promo-cards/${String(this.form.id)}`,
          fd,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );

        if (!resp || resp.status !== 200) {
          const m =
            (resp && resp.data && resp.data.message) || 'To αίτημα απέτυχε';
          throw new Error(m);
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα αποθηκεύτηκε',
        });
        await this.fetchItem();
      } catch (err) {
        const raw =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          (err && err.message) ||
          'To αίτημα απέτυχε';

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text:
            raw.indexOf('promo cards can be active') !== -1
              ? 'Επιτρέπονται έως 2 ενεργές κάρτες. Απενεργοποίησε πρώτα μία από τις ενεργές.'
              : raw,
        });
      }
      this.isSaving = false;
    },

    askToDelete() {
      this.isDeleteOpen = true;
    },

    async closeDeleteModal(confirmed) {
      this.isDeleteOpen = false;
      if (!confirmed) return;

      try {
        const { data } = await request.delete(
          `/home/promo-cards/${String(this.form.id)}`,
        );
        if (data && data.error) {
          throw new Error(data.message || 'Server error');
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα διαγράφηκε',
        });
        this.$router
          .push({ name: 'content.home.promo-cards.list' })
          .catch(() => {});
      } catch (err) {
        const msg = (err && err.message) || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg,
        });
      }
    },

    reset() {
      if (!this.original) return;

      this.form = {
        id: String(this.original.id),
        title_el: this.original.title_el || '',
        title_en: this.original.title_en || '',
        content_el: this.original.content_el || '',
        content_en: this.original.content_en || '',
        link: this.original.link || '',
        link_text_el: this.original.link_text_el || '',
        link_text_en: this.original.link_text_en || '',
        is_active: !!this.original.is_active,
        image: null,
      };
      this.preview = this.imageUrl || '';
      this.$validator.reset();
      this.errors.remove('image');
    },
  },
};
</script>
